<script setup lang="ts">
import { computed } from 'vue'
import AppComponent from '@/views/base/AppComponent.vue'
import { useHelpArticles } from '@/composables/useHelpArticles'

const { articles } = useHelpArticles()
const listedArticles = computed(() => articles.filter((a) => a.listed))
</script>

<template>
  <AppComponent title="Help Center" id="help-center">
    <div class="help-index">
      <div class="eyebrow">
        <span class="eyebrow-dot" />
        Documentation
      </div>

      <h1 class="page-title">Help Center</h1>
      <p class="page-subtitle">Everything you need to know about using Ginjer.ai</p>

      <div class="articles-grid">
        <RouterLink
          v-for="article in listedArticles"
          :key="article.slug"
          :to="`/help/${article.slug}`"
          class="article-card"
        >
          <div class="card-header">
            <UIcon :name="article.icon" class="card-icon" />
            <span class="card-label">{{ article.type }}</span>
          </div>
          <div class="card-body">
            <h2 class="card-title">{{ article.title }}</h2>
            <p class="card-desc">{{ article.description }}</p>
            <p class="card-meta">Last updated: {{ article.lastUpdated }}</p>
          </div>
        </RouterLink>
      </div>
    </div>
  </AppComponent>
</template>

<style scoped>
.help-index {
  max-width: 900px;
  margin: 0 auto;
  padding: 48px;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  border: 1px solid rgb(245 166 35 / 0.35);
  background: rgb(245 166 35 / 0.08);
  font-family: ui-monospace, monospace;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #a66907;
  margin-bottom: 20px;
}
.eyebrow-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #f5a623;
}

.page-title {
  font-size: 42px;
  font-weight: 800;
  color: var(--ui-text-highlighted);
  line-height: 1.15;
  letter-spacing: -0.02em;
  margin-bottom: 16px;
}

.page-subtitle {
  font-size: 17px;
  color: var(--ui-text-muted);
  margin-bottom: 48px;
  line-height: 1.65;
  max-width: 640px;
}

.articles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
}

.article-card {
  display: flex;
  flex-direction: column;
  background: var(--ui-bg);
  border: 1px solid var(--ui-border);
  border-radius: 12px;
  overflow: hidden;
  transition:
    transform 0.2s,
    box-shadow 0.2s;
}
.article-card:hover {
  transform: translateY(-3px);
  box-shadow:
    0 1px 0 rgb(15 28 46 / 0.04),
    0 12px 32px -12px rgb(15 28 46 / 0.18);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 20px;
  background: var(--ui-bg-elevated);
  border-bottom: 1px solid var(--ui-border);
}
.card-icon {
  font-size: 18px;
  color: #f5a623;
}
.card-label {
  font-family: ui-monospace, monospace;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #f5a623;
}

.card-body {
  padding: 20px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--ui-text-highlighted);
  margin-bottom: 8px;
  letter-spacing: -0.01em;
}

.card-desc {
  font-size: 13px;
  color: var(--ui-text-muted);
  line-height: 1.6;
  margin-bottom: 16px;
  flex: 1;
}

.card-meta {
  font-size: 11px;
  font-family: ui-monospace, monospace;
  color: var(--ui-text-dimmed);
}
</style>
